import React, { useState } from 'react';

interface WordFactoryProps {
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
  };
  onApplyWord: (word: string) => void;
}

const WordFactory: React.FC<WordFactoryProps> = ({ theme, onApplyWord }) => {
  const [activeTab, setActiveTab] = useState('builder');
  const [customWord, setCustomWord] = useState('');
  const [selectedParts, setSelectedParts] = useState({ prefix: '', root: '', suffix: '' });
  const [builtWords, setBuiltWords] = useState<string[]>([]);
  const [showCelebration, setShowCelebration] = useState('');

  // Enhanced vibrant theme
  const factoryTheme = {
    primary: '#FF6B35',     // Vibrant orange
    secondary: '#9B59B6',   // Rich purple  
    accent: '#F39C12',      // Bright yellow-orange
    success: '#2ECC71',     // Bright green
    pink: '#E91E63',        // Bright pink
    blue: '#3498DB',        // Bright blue
    teal: '#1ABC9C'         // Bright teal
  };

  const wordParts = {
    prefixes: [
      { part: 'un-', meaning: 'not', examples: ['unhappy', 'unlock'], color: factoryTheme.pink },
      { part: 're-', meaning: 'again', examples: ['rewrite', 'rebuild'], color: factoryTheme.blue },
      { part: 'pre-', meaning: 'before', examples: ['preview', 'prepare'], color: factoryTheme.success },
      { part: 'mis-', meaning: 'wrong', examples: ['mistake', 'misplace'], color: factoryTheme.secondary },
      { part: 'over-', meaning: 'too much', examples: ['overeat', 'overflow'], color: factoryTheme.primary },
      { part: 'under-', meaning: 'too little', examples: ['undercooked', 'underwater'], color: factoryTheme.teal }
    ],
    roots: [
      { part: 'act', meaning: 'do', examples: ['action', 'actor'], color: factoryTheme.primary },
      { part: 'port', meaning: 'carry', examples: ['transport', 'portable'], color: factoryTheme.blue },
      { part: 'graph', meaning: 'write', examples: ['autograph', 'photograph'], color: factoryTheme.success },
      { part: 'phone', meaning: 'sound', examples: ['telephone', 'microphone'], color: factoryTheme.secondary },
      { part: 'bio', meaning: 'life', examples: ['biology', 'biography'], color: factoryTheme.teal },
      { part: 'geo', meaning: 'earth', examples: ['geography', 'geology'], color: factoryTheme.pink }
    ],
    suffixes: [
      { part: '-ing', meaning: 'action happening', examples: ['running', 'singing'], color: factoryTheme.success },
      { part: '-ed', meaning: 'past action', examples: ['jumped', 'played'], color: factoryTheme.primary },
      { part: '-er', meaning: 'person who does', examples: ['teacher', 'writer'], color: factoryTheme.blue },
      { part: '-ly', meaning: 'in this way', examples: ['quickly', 'happily'], color: factoryTheme.secondary },
      { part: '-ful', meaning: 'full of', examples: ['hopeful', 'colorful'], color: factoryTheme.teal },
      { part: '-less', meaning: 'without', examples: ['hopeless', 'fearless'], color: factoryTheme.accent }
    ]
  };

  const wordCategories = {
    emotions: {
      icon: '😊',
      name: 'Feelings & Emotions',
      color: factoryTheme.pink,
      words: [
        { word: 'ecstatic', definition: 'extremely happy and excited', level: 'advanced' },
        { word: 'content', definition: 'happy and satisfied', level: 'beginner' },
        { word: 'anxious', definition: 'worried about something', level: 'intermediate' },
        { word: 'serene', definition: 'calm and peaceful', level: 'advanced' }
      ]
    },
    descriptive: {
      icon: '🎨',
      name: 'Describing Things',
      color: factoryTheme.accent,
      words: [
        { word: 'luminous', definition: 'giving off light, glowing', level: 'advanced' },
        { word: 'colossal', definition: 'extremely large', level: 'intermediate' },
        { word: 'vibrant', definition: 'bright and colorful', level: 'intermediate' },
        { word: 'magnificent', definition: 'extremely beautiful', level: 'beginner' }
      ]
    },
    action: {
      icon: '⚡',
      name: 'Action & Movement',
      color: factoryTheme.primary,
      words: [
        { word: 'dash', definition: 'to run quickly', level: 'beginner' },
        { word: 'soar', definition: 'to fly high', level: 'beginner' },
        { word: 'plummet', definition: 'to fall quickly', level: 'intermediate' },
        { word: 'saunter', definition: 'to walk slowly and relaxed', level: 'advanced' }
      ]
    }
  };

  const buildWord = () => {
    const { prefix, root, suffix } = selectedParts;
    if (root) {
      return `${prefix}${root}${suffix}`;
    }
    return '';
  };

  const getRandomWord = () => {
    const categories = Object.values(wordCategories);
    const randomCategory = categories[Math.floor(Math.random() * categories.length)];
    const randomWord = randomCategory.words[Math.floor(Math.random() * randomCategory.words.length)];
    return randomWord;
  };

  const generateWordChallenge = () => {
    const challenges = [
      "Create a word that means 'super happy'",
      "Build a word that means 'write again'", 
      "Make a word that means 'person who teaches'",
      "Create a word that means 'without fear'"
    ];
    return challenges[Math.floor(Math.random() * challenges.length)];
  };

  const saveBuiltWord = () => {
    const word = buildWord();
    if (word && !builtWords.includes(word)) {
      setBuiltWords(prev => [...prev, word]);
      setShowCelebration(word);
      setTimeout(() => setShowCelebration(''), 3000);
      onApplyWord(word);
    }
  };

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      background: `linear-gradient(135deg, ${factoryTheme.teal}, ${factoryTheme.blue}, ${factoryTheme.secondary})`,
      minHeight: '100vh',
      padding: '20px'
    }}>
      {/* Celebration Animation */}
      {showCelebration && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: `linear-gradient(45deg, ${factoryTheme.success}, ${factoryTheme.accent})`,
          color: 'white',
          padding: '32px',
          borderRadius: '20px',
          textAlign: 'center',
          zIndex: 1000,
          fontSize: '24px',
          fontWeight: 'bold',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          🎉 Word Created!
          <div style={{ fontSize: '18px', marginTop: '8px' }}>"{showCelebration}"</div>
        </div>
      )}

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: '800',
            background: `linear-gradient(45deg, ${factoryTheme.primary}, ${factoryTheme.secondary})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: '0 0 16px 0'
          }}>
            Word Factory
          </h1>
          <p style={{
            fontSize: '1.3rem',
            color: 'white',
            margin: 0,
            fontWeight: '600',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            Build amazing words by combining prefixes, roots, and suffixes!
          </p>
        </div>

        {/* Stats Bar */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          padding: '20px',
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: factoryTheme.primary }}>{builtWords.length}</div>
            <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '600' }}>Words Built</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: factoryTheme.success }}>
              {Object.values(wordParts).flat().length}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '600' }}>Parts Available</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: factoryTheme.blue }}>∞</div>
            <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '600' }}>Combinations</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          background: 'rgba(255,255,255,0.9)',
          padding: '8px',
          borderRadius: '16px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
        }}>
          {[
            { id: 'builder', name: 'Word Builder', icon: '🔧', color: factoryTheme.primary },
            { id: 'discover', name: 'Word Discovery', icon: '🔍', color: factoryTheme.blue },
            { id: 'challenge', name: 'Daily Challenge', icon: '🎯', color: factoryTheme.pink }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                background: activeTab === tab.id ? `linear-gradient(45deg, ${tab.color}, ${factoryTheme.accent})` : 'transparent',
                color: activeTab === tab.id ? 'white' : '#666',
                border: 'none',
                padding: '16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '700',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Word Builder Tab */}
        {activeTab === 'builder' && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 15px 50px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '32px',
              background: `linear-gradient(135deg, ${factoryTheme.primary}10, ${factoryTheme.blue}10)`,
              padding: '24px',
              borderRadius: '16px',
              border: `3px solid ${factoryTheme.primary}30`
            }}>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: factoryTheme.primary,
                margin: '0 0 12px 0'
              }}>
                Build Your Own Words!
              </h2>
              <p style={{
                fontSize: '1.1rem',
                color: '#666',
                margin: 0
              }}>
                Mix and match word parts like building blocks!
              </p>
            </div>

            {/* Word Parts Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '32px'
            }}>
              {Object.entries(wordParts).map(([partType, parts]) => (
                <div key={partType} style={{
                  background: `${factoryTheme[partType === 'prefixes' ? 'pink' : partType === 'roots' ? 'blue' : 'success']}05`,
                  borderRadius: '16px',
                  padding: '20px',
                  border: `3px solid ${factoryTheme[partType === 'prefixes' ? 'pink' : partType === 'roots' ? 'blue' : 'success']}30`
                }}>
                  <h3 style={{
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: factoryTheme[partType === 'prefixes' ? 'pink' : partType === 'roots' ? 'blue' : 'success'],
                    margin: '0 0 16px 0',
                    textTransform: 'capitalize'
                  }}>
                    {partType === 'roots' ? 'Root Words' : partType}
                  </h3>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '8px'
                  }}>
                    {parts.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedParts(prev => ({ 
                          ...prev, 
                          [partType.slice(0, -1)]: selectedParts[partType.slice(0, -1) as keyof typeof selectedParts] === item.part ? '' : item.part
                        }))}
                        style={{
                          background: selectedParts[partType.slice(0, -1) as keyof typeof selectedParts] === item.part 
                            ? item.color 
                            : 'white',
                          color: selectedParts[partType.slice(0, -1) as keyof typeof selectedParts] === item.part 
                            ? 'white' 
                            : '#333',
                          border: `2px solid ${item.color}`,
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          transition: 'all 0.3s ease',
                          fontWeight: '600'
                        }}
                      >
                        <div style={{ fontWeight: '800', marginBottom: '2px' }}>{item.part}</div>
                        <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{item.meaning}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Word Assembly */}
            <div style={{
              background: `linear-gradient(135deg, ${factoryTheme.accent}10, ${factoryTheme.primary}10)`,
              borderRadius: '20px',
              padding: '32px',
              textAlign: 'center',
              border: `3px solid ${factoryTheme.accent}`
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: factoryTheme.accent,
                margin: '0 0 20px 0'
              }}>
                Word Creation Station
              </h3>
              
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                marginBottom: '24px',
                border: `3px solid ${factoryTheme.primary}`
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '800',
                  color: buildWord() ? factoryTheme.primary : '#ccc',
                  marginBottom: '12px',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {buildWord() || 'Select parts to build!'}
                </div>
                
                {buildWord() && (
                  <button
                    onClick={saveBuiltWord}
                    style={{
                      background: `linear-gradient(45deg, ${factoryTheme.success}, ${factoryTheme.teal})`,
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '700'
                    }}
                  >
                    💾 Save This Word
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Word Discovery Tab */}
        {activeTab === 'discover' && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 15px 50px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: factoryTheme.blue,
                margin: '0 0 12px 0'
              }}>
                Discover Amazing Words!
              </h2>
              
              <button
                onClick={() => {
                  const randomWord = getRandomWord();
                  onApplyWord(randomWord.word);
                  setShowCelebration(randomWord.word);
                  setTimeout(() => setShowCelebration(''), 3000);
                }}
                style={{
                  background: `linear-gradient(45deg, ${factoryTheme.secondary}, ${factoryTheme.pink})`,
                  color: 'white',
                  border: 'none',
                  padding: '16px 32px',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  margin: '20px 0'
                }}
              >
                🎲 Random Word
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {Object.entries(wordCategories).map(([categoryKey, category]) => (
                <div key={categoryKey} style={{
                  background: `${category.color}05`,
                  borderRadius: '16px',
                  padding: '20px',
                  border: `3px solid ${category.color}30`
                }}>
                  <h3 style={{
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: category.color,
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>{category.icon}</span>
                    {category.name}
                  </h3>
                  
                  {category.words.map((wordItem, index) => (
                    <div 
                      key={index}
                      style={{
                        background: 'white',
                        border: `2px solid ${category.color}30`,
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        onApplyWord(wordItem.word);
                        setShowCelebration(wordItem.word);
                        setTimeout(() => setShowCelebration(''), 3000);
                      }}
                    >
                      <div style={{
                        fontWeight: '700',
                        fontSize: '1rem',
                        color: '#333',
                        marginBottom: '4px'
                      }}>
                        {wordItem.word}
                      </div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#666'
                      }}>
                        {wordItem.definition}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Challenge Tab */}
        {activeTab === 'challenge' && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 15px 50px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: factoryTheme.pink,
                margin: '0 0 12px 0'
              }}>
                Daily Word Challenge!
              </h2>
            </div>

            <div style={{
              background: `${factoryTheme.pink}10`,
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center',
              border: `3px solid ${factoryTheme.pink}30`
            }}>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#333',
                fontWeight: '600'
              }}>
                "{generateWordChallenge()}"
              </div>
              
              <button
                onClick={() => {
                  const challenge = generateWordChallenge();
                  alert(`Challenge: ${challenge}\n\nUse the Word Builder tab to create your answer!`);
                }}
                style={{
                  background: `linear-gradient(45deg, ${factoryTheme.pink}, ${factoryTheme.accent})`,
                  color: 'white',
                  border: 'none',
                  padding: '16px 32px',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: '700'
                }}
              >
                🎯 New Challenge
              </button>
            </div>
          </div>
        )}

        {/* Built Words Collection */}
        {builtWords.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '20px',
            padding: '24px',
            marginTop: '32px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: factoryTheme.primary,
              margin: '0 0 16px 0',
              textAlign: 'center'
            }}>
              Your Word Collection ({builtWords.length} words!)
            </h3>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              justifyContent: 'center'
            }}>
              {builtWords.map((word, index) => (
                <div
                  key={index}
                  style={{
                    background: `linear-gradient(45deg, ${factoryTheme.success}, ${factoryTheme.teal})`,
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}
                >
                  {word}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Word Input */}
        <div style={{ 
          marginTop: '32px',
          background: 'rgba(255,255,255,0.95)',
          padding: '24px',
          borderRadius: '16px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: '700',
            color: factoryTheme.teal,
            margin: '0 0 16px 0',
            textAlign: 'center'
          }}>
            Add Your Own Word
          </h3>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input
              type="text"
              value={customWord}
              onChange={(e) => setCustomWord(e.target.value)}
              placeholder="Type any word you discovered..."
              style={{
                flex: 1,
                padding: '16px',
                border: `3px solid ${factoryTheme.teal}`,
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}
            />
            <button
              onClick={() => {
                if (customWord.trim()) {
                  onApplyWord(customWord.trim());
                  setBuiltWords(prev => [...prev, customWord.trim()]);
                  setShowCelebration(customWord.trim());
                  setTimeout(() => setShowCelebration(''), 3000);
                  setCustomWord('');
                }
              }}
              disabled={!customWord.trim()}
              style={{
                background: customWord.trim() ? 
                  `linear-gradient(45deg, ${factoryTheme.teal}, ${factoryTheme.blue})` : 
                  '#ccc',
                color: 'white',
                border: 'none',
                padding: '16px 24px',
                borderRadius: '12px',
                cursor: customWord.trim() ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: '700'
              }}
            >
              Add Word
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordFactory;